import type { RootState } from "../state";
import { augmentStoreWithAppleHiddenWebAudio } from "./appleBackgroundWebAudio";
// 移除 HTML5 链在 iOS 的参与，保持 WA-only
import message from "@/utils/message/messageFactory";
import { getToken } from "@/utils/auth";
import { getConfig } from "@/config";
import { nextTick } from "vue";
import { $t, transformI18n } from "@/plugins/i18n";
import { HTTP_HEADERS } from "@/config/httpHead";
import { emitter } from "@/utils/emitter";
// filed 为ws接口中的type | value为取的字段
export const dashbordFiled = [
  { label: "commentCount", field: "commentCount", value: "count" },
  { label: "enter", field: "enter", value: "count" },
  { label: "current", field: "room_statistics", value: "display_long" },
  { label: "replyRate", field: "replyRate", value: "rate" },
  { label: "followCount", field: "followCount", value: "count" }
];
// WebSocket连接相关方法

// 🚀 新增：离开直播间时的WebSocket管理
export function leaveRoomWebSocket(
  this: RootState & { [key: string]: any },
  roomId: string
) {
  if (!roomId || !this.roomStates.has(roomId)) {
    console.warn(`⚠️ leaveRoomWebSocket: 房间${roomId}不存在`);
    return;
  }

  const state = this.roomStates.get(roomId);
  // 离开直播间，关闭WebSocket连接但保留数据

  // 1. 关闭公屏信息WebSocket
  if (state.commentWs) {
    state.commentWs.close();
    state.commentWs = null;
    // 设置为手动解绑，防止自动重连
    state.isManuallyUnbound = true;
  }

  // 2. 关闭队列WebSocket
  if (state.audioQueueWs) {
    state.audioQueueWs.close();
    state.audioQueueWs = null;
  }

  // 3. 清理重连计时器
  if (state.commentWsReconnectTimer) {
    clearTimeout(state.commentWsReconnectTimer);
    state.commentWsReconnectTimer = null;
  }
  if (state.audioQueueWsReconnectTimer) {
    clearTimeout(state.audioQueueWsReconnectTimer);
    state.audioQueueWsReconnectTimer = null;
  }

  // 4. 重置连接状态但保留绑定状态
  state.isConnecting = false;
  // 保留 state.isBound 状态，这样进入时知道之前是绑定的

  // 5. 保留重要数据，确保数据持久性
  // - state.publicMessages 保留（公屏信息）
  // - state.sessionId 保留（会话ID）
  // - state.roomLink 保留（直播链接）
  // - state.isBound 保留（绑定状态）

  console.log(
    `✅ 房间${roomId}WebSocket已断开，数据已保留：公屏消息${state.publicMessages.length}条，绑定状态${state.isBound}`
  );
}

// 🚀 新增：进入直播间时的WebSocket管理
export async function enterRoomWebSocket(
  this: RootState & { [key: string]: any },
  roomId: string,
  productList?: any
) {
  if (!roomId) {
    return;
  }

  if (!this.roomStates.has(roomId)) {
    // 如果房间不存在，尝试从localStorage恢复或创建默认状态
    const savedState = this.loadStateFromStorage();
    if (!savedState || !this.roomStates.has(roomId)) {
      return;
    }
  }

  const state = this.roomStates.get(roomId);
  // 挂载苹果隐藏态 WebAudio 分支（仅挂方法，不主动创建）
  try {
    augmentStoreWithAppleHiddenWebAudio(this as any);
  } catch {}

  // 新增：检查是否为页面刷新状态，确保只有主动进入才恢复功能
  const wasPageRefreshed = state.isPageRefreshed;
  if (wasPageRefreshed) {
    console.log(`🔄 房间${roomId}检测到页面刷新状态，开始恢复所有功能`);
    // 注意：不在这里清除标记，让WebSocket连接时处理
  }

  // 如果传入了productList参数，使用它来更新状态信息
  if (productList) {
    // 关键修复：仅更新必要的状态信息，不覆盖已有的产品列表
    if (
      !state.productList ||
      !state.productList.products ||
      state.productList.products.length === 0
    ) {
      // 只有在没有产品列表时才完全覆盖
      state.productList = productList;
    } else {
      // 如果已有产品列表，仅更新状态信息
      if (productList.status) {
        state.productList.status = productList.status;
      }
      if (productList.intervalTime !== undefined) {
        state.productList.intervalTime = productList.intervalTime;
      }
      if (productList.name) {
        state.productList.name = productList.name;
      }
      if (productList.roomLink) {
        state.productList.roomLink = productList.roomLink;
      }
      console.log(
        `📦 enterRoomWebSocket: 保留已存在的产品列表数据，共${state.productList.products.length}个产品`
      );
    }

    // 如果productList中有roomLink，设置到state中
    if (productList.roomLink) {
      state.roomLink = productList.roomLink;
      // 修复：只有在开播状态下才设置绑定状态
      if (state.productList.status === "live") {
        state.isBound = true;
      } else {
        state.isBound = false;
        console.log(`🔌 房间${roomId}有链接但未开播，不设置绑定状态`);
      }
    }
  }

  // 2. 检查sessionId（如果没有的话）
  // 注意：这里主要是处理页面刷新或重新进入房间的情况
  // 正常开始直播时已经从 startProductsLive 接口获取了 sessionId
  if (!state.sessionId) {
    console.warn(`⚠️ 房间${roomId}缺少sessionId，将影响音频队列WebSocket连接`);
    // 不再调用已废弃的接口，静默处理
  }

  // 3. 如果是直播状态，建立WebSocket连接
  if (state.productList?.status === "live") {
    // 检查是否需要重新连接（页面刷新后）
    const needsReconnect = state.needsReconnect;
    if (needsReconnect) {
      console.log(`🔄 房间${roomId}需要重新连接WebSocket（页面刷新后）`);
      state.needsReconnect = false; // 清除标记
    }

    // 4. 建立队列WebSocket连接
    if (state.sessionId) {
      try {
        await this.initAudioQueueWebSocket(roomId);
        if (needsReconnect) {
          console.log(`✅ 房间${roomId}音频队列WebSocket重新连接成功`);
        }
      } catch {
        // 静默处理WebSocket连接失败
      }
    }

    // 4. 检查是否有roomLink，如果有则建立公屏WebSocket
    if ((state.productList as any)?.roomLink) {
      // 设置roomLink到state中
      state.roomLink = (state.productList as any).roomLink;
      // 修复：只有在开播状态下才设置绑定状态并建立连接
      if (state.productList?.status === "live") {
        state.isBound = true;
        state.isManuallyUnbound = false;

        try {
          await this.bindCommentWebSocket();
          if (needsReconnect) {
            console.log(`✅ 房间${roomId}公屏WebSocket重新连接成功`);
          }
        } catch (error) {
          console.error(`公屏WebSocket连接失败:`, error);
        }
      } else {
        state.isBound = false;
        console.log(`🔌 房间${roomId}有链接但未开播，跳过WebSocket连接`);
      }
    } else if (state.isBound && state.roomLink) {
      // 如果之前是绑定状态，重新建立公屏WebSocket
      state.isManuallyUnbound = false; // 取消手动解绑标记
      try {
        await this.bindCommentWebSocket();
        if (needsReconnect) {
          console.log(`✅ 房间${roomId}公屏WebSocket重新连接成功`);
        }
      } catch (error) {
        console.error(`公屏WebSocket重连失败:`, error);
      }
    }
  }
}

export async function fetchSessionId(
  this: RootState & { [key: string]: any },
  roomId: string
) {
  const state = this.roomStates.get(roomId);
  if (!state) {
    return false;
  }

  // 修复：移除对 getLiveSessionIdByRoomId 接口的调用
  // 现在开始直播时可以直接从 startProductsLive 接口获取 sessionId
  // 如果到这里说明 sessionId 确实不存在，应该是配置问题
  console.warn(
    `⚠️ 尝试获取房间${roomId}的sessionId，但此接口已废弃。请确保开始直播时正确设置了sessionId`
  );

  // 检查是否已经有 sessionId（可能在其他地方设置了）
  if (state.sessionId) {
    console.log(`✅ 房间${roomId}已有sessionId: ${state.sessionId}`);
    return true;
  }

  // 如果确实没有 sessionId，返回 false
  console.error(`❌ 房间${roomId}缺少sessionId，无法建立WebSocket连接`);
  return false;
}

// 切换绑定状态
export async function toggleBind(
  this: RootState & { [key: string]: any }
): Promise<{
  success: boolean;
  message: string;
}> {
  return new Promise(async resolve => {
    if (!this.currentRoomState)
      return resolve({
        success: false,
        message: "No current room"
      });
    const state = this.currentRoomState;

    if (state.isBound) {
      // 当前已绑定，执行解绑
      try {
        this.unbindCommentWebSocket();
        resolve({
          success: true,
          message: "myLiveRoomPage.messages.unbindLiveLinkSuccess"
        });
      } catch {
        resolve({
          success: false,
          message: "myLiveRoomPage.messages.unbindLiveLinkFailed"
        });
      }
    } else {
      // 当前未绑定，执行绑定前先验证必填信息
      const roomLink = state.roomLink?.trim();
      const livePlatform = state.livePlatform?.trim();

      // 验证直播链接
      if (!roomLink) {
        resolve({
          success: false,
          message: "myLiveRoomPage.messages.enterLiveLink"
        });
        return;
      }

      // 验证直播平台
      if (!livePlatform) {
        resolve({
          success: false,
          message: "myLiveRoomPage.messages.selectLivePlatform"
        });
        return;
      }

      // 验证直播链接格式（简单验证是否包含http）
      // if (!roomLink.startsWith("http://") && !roomLink.startsWith("https://")) {
      //   message.error("请输入有效的直播链接（以http://或https://开头）");
      //   return;
      // }

      // 绑定不需要保存配置，直接建立WebSocket连接
      try {
        await this.bindCommentWebSocket();
        resolve({
          success: true,
          message: "myLiveRoomPage.messages.bindLiveLinkSuccess"
        });
      } catch (error) {
        console.error("绑定WebSocket失败:", error);
        resolve({
          success: false,
          message: "myLiveRoomPage.messages.bindWebSocketFailed"
        });
      }
    }
  });
}

export function bindCommentWebSocket(this: RootState & { [key: string]: any }) {
  if (!this.currentRoomState) return Promise.reject("No current room");
  const state = this.currentRoomState;

  if (
    state.commentWs &&
    (state.commentWs.readyState === WebSocket.OPEN ||
      state.commentWs.readyState === WebSocket.CONNECTING)
  ) {
    return Promise.resolve(true);
  }
  if (state.commentWs) {
    state.commentWs.close();
  }

  // 初始化重连计数器
  if (!state.commentWsReconnectCount) {
    state.commentWsReconnectCount = 0;
  }

  // 清除主动解绑标记，表示现在要主动连接
  state.isManuallyUnbound = false;
  state.isConnecting = true;
  const token = getToken();
  const wsProtocol = window.location.protocol === "https:" ? "wss:" : "ws:";
  const wsHost = window.location.host;
  let wsUrl = "";
  if (import.meta.env.VITE_API_BASE_WS) {
    wsUrl = `${import.meta.env.VITE_API_BASE_WS}/live/comment?token=${token}`;
  } else {
    wsUrl = `${wsProtocol}//${wsHost}${HTTP_HEADERS.Ws}/live/comment?token=${token}`;
  }

  return new Promise((resolve, reject) => {
    try {
      const ws = new WebSocket(wsUrl);
      state.commentWs = ws;
      ws.onopen = () => {
        state.isConnecting = false;
        state.isBound = true;
        state.commentWsReconnectCount = 0;

        // 🔧 启动心跳机制
        this.startCommentWsHeartbeat();

        // 如果已经开播，立即发送绑定消息；否则等待开播
        if (state.productList?.status === "live") {
          setTimeout(() => this.sendBindMessage(), 500);
        }

        resolve(true);
      };

      ws.onmessage = event => {
        try {
          const data = JSON.parse(event.data);
          // 🔧 处理心跳响应
          if (data.type === "pong") {
            console.log("💓 收到公屏WebSocket心跳响应 (pong)");
            return;
          }
          if (data.type === "comment") {
            state.publicMessages.push({
              id: Date.now(),
              username: data.username,
              content: data.content,
              status: "pending",
              commentId: data.commentId
            });
            nextTick(() => this.scrollToBottom());
          } else if (data.type === "comment_update") {
            const commentToUpdate = state.publicMessages.find(
              msg =>
                (msg.commentId && msg.commentId === data.commentId) ||
                (!msg.commentId && msg.content === data.content)
            );
            if (commentToUpdate) {
              commentToUpdate.status = data.status;
              commentToUpdate.aiReply = data.aiReply;
              if (data.commentId && !commentToUpdate.commentId) {
                commentToUpdate.commentId = data.commentId;
              }
              nextTick(() => this.scrollToBottom());
            }
          } else if (data.status === "error" && data.message) {
            // 新增：处理错误消息，显示在公屏上
            state.publicMessages.push({
              id: Date.now(),
              username: transformI18n($t("public.system")),
              content: data.message,
              status: "error",
              commentId: null,
              isSystemMessage: true
            });
            nextTick(() => this.scrollToBottom());
          } else if (data.status === "success" && data.message) {
            // 新增：处理成功消息，显示在公屏上
            state.publicMessages.push({
              id: Date.now(),
              username: transformI18n($t("public.system")),
              content: data.message,
              status: "success",
              commentId: null,
              isSystemMessage: true
            });
            nextTick(() => this.scrollToBottom());
          } else if (dashbordFiled.some(item => item.field === data.type)) {
            const findIndex = dashbordFiled.find(
              item => item.field === data.type
            );
            state.currentDashbordForm[findIndex.field] = data[findIndex.value];
            // console.log("data.type", data.type, findIndex, );
          } else if (data.status == "info" && data.message) {
            message.success(data.message);
          }
        } catch (error) {
          console.error("处理公屏消息时出错:", error);
        }
      };

      ws.onerror = error => {
        state.isConnecting = false;
        state.commentWs = null;

        // 如果是首次连接失败，则reject
        if (state.commentWsReconnectCount === 0) {
          reject(error);
        }
      };

      ws.onclose = () => {
        state.isConnecting = false;
        state.commentWs = null;

        // 🔧 停止心跳机制
        this.stopCommentWsHeartbeat();

        // 检查是否为主动解绑
        if (state.isManuallyUnbound) {
          state.isBound = false;
          return;
        }

        // 如果已绑定，持续重连
        if (state.isBound) {
          state.commentWsReconnectCount++;
          const retryDelay = Math.min(
            1000 * Math.pow(2, Math.min(state.commentWsReconnectCount - 1, 6)),
            30000
          ); // 指数退避，最大30秒，但限制指数增长避免过长延迟

          setTimeout(() => {
            if (state.isBound && !state.commentWs && !state.isManuallyUnbound) {
              this.bindCommentWebSocket().catch(() => {
                // 静默处理重连失败
              });
            }
          }, retryDelay);
        }
      };
    } catch (error) {
      state.isConnecting = false;
      state.isBound = false;
      reject(error);
    }
  });
}

export function sendBindMessage(this: RootState & { [key: string]: any }) {
  if (!this.currentRoomState) return;
  const state = this.currentRoomState;

  if (!state.commentWs || state.commentWs.readyState !== WebSocket.OPEN) {
    return;
  }

  // 只有在开播状态下才发送绑定消息
  if (state.productList?.status === "live") {
    const bindMessage = {
      platform: state.livePlatform,
      action: "bind",
      businessRoomId: state.productList.id,
      url: state.roomLink
    };
    state.commentWs.send(JSON.stringify(bindMessage));
    console.log(`📤 发送绑定消息:`, bindMessage);
  } else {
    console.log(`⏸️ 未开播，暂不发送绑定消息`);
  }
}

export function unbindCommentWebSocket(
  this: RootState & { [key: string]: any }
) {
  if (!this.currentRoomState) return;
  const state = this.currentRoomState;

  // 🔧 停止心跳机制
  this.stopCommentWsHeartbeat();

  // 标记为主动解绑，防止重连
  state.isManuallyUnbound = true;

  if (state.commentWs) {
    state.commentWs.close();
    state.commentWs = null;
  }
  state.isBound = false;
  state.commentWsReconnectCount = 0; // 重置重连计数器
}

export async function initAudioQueueWebSocket(
  this: RootState & { [key: string]: any },
  roomId: string,
  sessionId?: string,
  isExperience: boolean = false
) {
  if (!roomId || !this.roomStates.has(roomId))
    return Promise.reject("No room context for Audio WS");
  const state = this.roomStates.get(roomId);

  if (
    state.audioQueueWs &&
    (state.audioQueueWs.readyState === WebSocket.OPEN ||
      state.audioQueueWs.readyState === WebSocket.CONNECTING)
  ) {
    return Promise.resolve(true);
  }
  if (state.audioQueueWs) {
    state.audioQueueWs.close();
  }

  // 检查必要参数
  if (!sessionId && !state.sessionId) {
    // 修复：不再尝试获取sessionId，因为已经废弃了相关接口
    console.error(
      `❌ 房间${roomId}缺少sessionId，无法建立音频队列WebSocket连接`
    );
    return Promise.reject("缺少sessionId，请先开始直播");
  }

  const params = {
    roomId: state.roomId || state.productList.id,
    sequence: "0",
    sessionId: sessionId || state.sessionId
  };

  const token = getToken();
  if (!token && !isExperience) {
    return Promise.reject("No token for WebSocket connection");
  }

  const wsProtocol = window.location.protocol === "https:" ? "wss:" : "ws:";
  const wsHost = window.location.host;
  let wsUrl;
  if (isExperience) {
    if (import.meta.env.VITE_API_BASE_WS) {
      wsUrl = `${import.meta.env.VITE_API_BASE_WS}/live/liveRoomExperience?token=${token}&roomId=${encodeURIComponent(params.roomId)}&sessionId=${params.sessionId}`;
    } else {
      wsUrl = `${wsProtocol}//${wsHost}${HTTP_HEADERS.Ws}/live/liveRoomExperience?token=${token}&roomId=${encodeURIComponent(params.roomId)}&sessionId=${params.sessionId}`;
    }
  } else {
    if (import.meta.env.VITE_API_BASE_WS) {
      wsUrl = `${import.meta.env.VITE_API_BASE_WS}/live/audio-queue-live-websocket?token=${token}&roomId=${encodeURIComponent(params.roomId)}`;
    } else {
      wsUrl = `${wsProtocol}//${wsHost}${HTTP_HEADERS.Ws}/live/audio-queue-live-websocket?token=${token}&roomId=${encodeURIComponent(params.roomId)}`;
    }
  }
  return new Promise((resolve, reject) => {
    const ws = new WebSocket(wsUrl);
    state.audioQueueWs = ws;
    // 将socket实例存到window，方便调试socket断联情况
    (window as any).audioQueueWs = ws;

    const resetNoAudioDataTimer = () => {
      if (isExperience) return;
      if ((state as any).audioQueueWsNoDataTimer) {
        clearTimeout((state as any).audioQueueWsNoDataTimer);
        (state as any).audioQueueWsNoDataTimer = null;
      }
      (state as any).audioQueueWsNoDataTimer = setTimeout(() => {
        if (state.productList?.status === "live") {
          emitter.emit("startPlayHistory");
        }
      }, 15000);
    };

    ws.onopen = () => {
      // 重置重连计数器
      state.audioQueueWsReconnectCount = 0;

      resetNoAudioDataTimer();

      // 移除所有去重保护逻辑，让每个后端推送的音频都能正常播放
      console.log(`🔄 移除去重保护，接受所有后端音频消息`);

      // 强化检查：是否是刷新恢复的情况
      const queueExists = state.queue && Array.isArray(state.queue);
      const queueLength = queueExists ? state.queue.length : 0;

      // 强化判断：任何情况下有队列就认为是恢复
      const hasExistingQueue = queueLength > 0;
      const shouldHandleRefresh =
        hasExistingQueue && state.isPageRefreshed && !!state.readyToRestore;

      if (shouldHandleRefresh) {
        // 页面刷新恢复情况：保留队列和进度，从刷新前位置继续
        console.log(
          `🔄 页面刷新恢复WebSocket，保留队列(${queueLength}项)和播放进度`
        );

        // 详细调试信息：检查当前状态
        console.log(`📊 刷新恢复调试信息:`);
        console.log(`   - 队列长度: ${state.queue.length}`);
        console.log(`   - 当前索引: ${state.currentIndex}`);
        console.log(`   - 播放状态: ${state.isPlaying}`);
        console.log(`   - 准备恢复: ${state.readyToRestore}`);

        // 重置播放器状态但保留队列数据
        state.isPlaying = false;

        const existingPlayer = (state as any).howlerPlayer;
        if (existingPlayer) {
          let waitingForRestore = false;
          try {
            const checkRestore = existingPlayer.isWaitingForProgressRestore;
            if (typeof checkRestore === "function") {
              waitingForRestore = !!checkRestore.call(existingPlayer);
            }
          } catch {}

          // 🔧 关键修复：增加对 state.waitingForRestore 的检查，防止误销毁保留的播放器
          const stateWaitingForRestore = !!(state as any).waitingForRestore;
          if (
            waitingForRestore ||
            state.readyToRestore ||
            stateWaitingForRestore
          ) {
            console.log(
              `🎵 检测到正在等待进度恢复的Howler播放器，保留实例以便续播 (playerWaiting=${waitingForRestore}, stateReady=${state.readyToRestore}, stateWaiting=${stateWaitingForRestore})`
            );
          } else {
            console.log(
              `🔍 [DESTROY_TRACE] WebSocket重连销毁播放器 - 调用栈:`,
              new Error().stack
            );
            console.log(`清理旧的Howler播放器`);
            existingPlayer.stop();
            existingPlayer.destroy();
            (state as any).howlerPlayer = null;
          }
        }

        // 🔧 关键修复：恢复播放时需要告诉后端继续发送音频
        // 发送参数让后端知道需要继续播放
        console.log(`📡 恢复播放模式：告知后端继续发送音频数据`);

        // 发送恢复参数，包含当前播放状态
        const resumeParams = {
          roomId: roomId,
          sessionId: sessionId || state.sessionId,
          action: "resume", // 标识这是恢复播放
          currentIndex: state.currentIndex, // 当前播放索引
          queueLength: state.queue.length // 已有队列长度
        };

        // 发送恢复消息给后端
        ws.send(JSON.stringify(resumeParams));
        console.log(`📤 发送恢复播放参数:`, resumeParams);

        state.isPageRefreshed = false; // 清除刷新标记

        // 新增：检查是否enterRoom已经准备好恢复播放
        if (state.readyToRestore && this.currentRoomId === roomId) {
          console.log(`🎵 WebSocket连接完成，开始恢复播放`);
          state.readyToRestore = false; // 清除恢复标记

          // 延迟一点点时间确保状态稳定
          setTimeout(() => {
            const currentState = this.roomStates.get(roomId);
            if (currentState) {
              // 🔧 关键修复：检查播放器实际播放状态而不只看state.isPlaying
              const existingPlayer = (currentState as any).howlerPlayer;
              let isWaitingForRestore = false;
              let isActuallyPlaying = false;

              if (existingPlayer) {
                try {
                  const checkRestore =
                    existingPlayer.isWaitingForProgressRestore;
                  if (typeof checkRestore === "function") {
                    isWaitingForRestore = !!checkRestore.call(existingPlayer);
                  }
                } catch {}

                try {
                  const status = existingPlayer.getStatus();
                  isActuallyPlaying = !!status.isPlaying;
                } catch {}
              }

              // 🔧 新逻辑：只要播放器不在实际播放状态就尝试恢复
              const shouldTriggerRestore =
                !isActuallyPlaying && !isWaitingForRestore;

              console.log(
                `🔄 WebSocket恢复播放检查: state.isPlaying=${currentState.isPlaying}, actuallyPlaying=${isActuallyPlaying}, waitingForRestore=${isWaitingForRestore}, shouldTrigger=${shouldTriggerRestore}`
              );

              if (shouldTriggerRestore) {
                console.log(
                  `🔄 触发恢复播放: 队列长度${currentState.queue.length}, 当前索引${currentState.currentIndex}, 有播放器${!!existingPlayer}`
                );
                this.playNext(roomId);
              } else {
                console.log(`🔄 跳过恢复播放: 已在播放或等待恢复中`);
              }
            }
          }, 100);
        }
      } else if (hasExistingQueue) {
        if (state.isPageRefreshed) {
          console.log(`⚠️ 检测到刷新标记但缺少恢复信号，重置标记以避免误清理`);
          state.isPageRefreshed = false;
        }
        // 🔧 修复：普通重连时也需要告诉后端继续发送音频
        console.log(`🔄 WebSocket重连，通知后端继续: 队列长度${queueLength}`);

        // 发送继续参数
        const continueParams = {
          roomId: roomId,
          sessionId: sessionId || state.sessionId,
          action: "continue", // 标识这是继续播放
          currentIndex: state.currentIndex,
          queueLength: state.queue.length
        };

        ws.send(JSON.stringify(continueParams));
        console.log(`📤 发送继续播放参数:`, continueParams);
      } else {
        // 🔧 修复：新直播开始时也需要发送参数让后端开始发送音频
        console.log(`🆕 新直播开始，发送初始化参数`);

        const initParams = {
          roomId: roomId,
          sessionId: sessionId || state.sessionId,
          action: "start" // 标识这是新开始
        };

        ws.send(JSON.stringify(initParams));
        console.log(`📤 发送初始化参数:`, initParams);
      }
      resolve(true);
    };

    ws.onmessage = event => {
      try {
        const data = JSON.parse(event.data);

        if (data.originalText && data.voiceUrl) {
          resetNoAudioDataTimer();
          emitter.emit("stopPlayHistory");
          // 加强：切换保护期过滤（严格白名单 + 延迟清标记）
          let bypassDedup = false;
          if (state.lastProductSwitchTime && state.switchingProductId) {
            const nowTs = Date.now();
            // const messageTime = data.createTime || nowTs;
            const timeSinceSwitch = nowTs - state.lastProductSwitchTime;
            const PROTECT_MS = Number((state as any).switchProtectMs ?? 100);

            if (timeSinceSwitch <= PROTECT_MS) {
              // 保护期内：仅允许当前新产品ID的音频通过，其余全部丢弃
              if (
                !data.productId ||
                data.productId !== state.switchingProductId
              ) {
                console.log(
                  `🔒 保护期丢弃非当前产品音频: pid=${data.productId || "无"} -> 期望=${state.switchingProductId}`
                );
                return;
              }
              // 记录新产品音频计数，满足条件后再清除标记
              const prevCount = (state as any).newAudioAcceptedCount || 0;
              bypassDedup = prevCount === 0; // 首条新产品音频：绕过去重
              (state as any).newAudioAcceptedCount = prevCount + 1;
              if ((state as any).newAudioAcceptedCount >= 2) {
                console.log(`✅ 保护期确认新产品音频充足，清除切换标记`);
                state.switchingProductId = null;
                state.lastProductSwitchTime = null;
                delete (state as any).newAudioAcceptedCount;
              }
            } else {
              // 超过保护期：无论是否收到足量新音频，清除标记避免长期拦截
              console.log(`⏱️ 保护期结束，清除切换标记`);
              state.switchingProductId = null;
              state.lastProductSwitchTime = null;
              delete (state as any).newAudioAcceptedCount;
            }
          }

          // 使用高性能队列处理音频数据（打印紧急检测日志）
          try {
            const rawVoiceNo = (data as any)?.voiceNo;
            const isUrgentInbound = Number(rawVoiceNo) === -1;
            const snippet = (data?.originalText || "").substring(0, 30);
            console.warn(
              `[urgent-check] inbound msg id=${data?.id || "NA"} voiceNo=${rawVoiceNo} isUrgent=${isUrgentInbound} pid=${data?.productId || "NA"} text="${snippet}..."`
            );
          } catch {}

          // 使用高性能队列处理音频数据
          const audioData = {
            id: data.id || String(Date.now()),
            text: data.originalText,
            audioUrl: data.voiceUrl,
            status: "pending",
            commentId: data.commentId || null,
            voiceNo: data.voiceNo || 0, // 新增：支持紧急插播
            createTime: data.createTime || Date.now(), // 新增：支持时间排序
            productId: data.productId || null,
            bypassDedup,
            arrivalTs: Date.now()
          };

          try {
            const snippet2 = (audioData.text || "").substring(0, 30);
            console.warn(
              `[urgent-check] enqueue voiceNo=${audioData.voiceNo} isUrgent=${Number(audioData.voiceNo) === -1} ver=${state.switchVersion ?? 0} pid=${audioData.productId || "NA"} text="${snippet2}..."`
            );
          } catch {}

          this.addAudioToQueue(roomId, audioData);
          try {
            // 标记房间使用 UniversalWebAudio
            try {
              const g: any = globalThis as any;
              if (!g.__universalWebAudioRooms)
                g.__universalWebAudioRooms = new Set();
              g.__universalWebAudioRooms.add(roomId);
            } catch {}
          } catch {}

          // 避免重复调用playNext，检查是否已有音频播放器在运行
          // 详细检查播放触发条件
          const isCurrentRoom = this.currentRoomId === roomId;
          const hasHowlerPlayer = !!(state as any).howlerPlayer;
          const isPlaying = state.isPlaying;

          // 强化判断：如果有音频正在播放，绝对不允许触发新播放
          const isActuallyPlaying =
            hasHowlerPlayer &&
            (state as any).howlerPlayer &&
            (state as any).howlerPlayer.getStatus().isPlaying;

          if (!isPlaying) {
            const qlen = state.queue?.length ?? 0;

            // UniversalWebAudio：只在队列为空时才重置索引
            if (qlen === 0) {
              state.currentIndex = 0;
              console.log(`🔧 [UniversalWebAudio] 队列为空，重置索引到0`);
            } else {
              // 保持现有索引，让UniversalWebAudio自己管理进度
              console.log(
                `🔧 [UniversalWebAudio] 保持currentIndex=${state.currentIndex}，队列长度=${qlen}`
              );
            }
          }

          // 修复"状态不一致导致卡住"：当 store 标记正在播放但播放器实际未播放时，仍允许触发播放
          const shouldAllowWebSocketPlay =
            isCurrentRoom &&
            (!isPlaying || !isActuallyPlaying) &&
            this.getQueueLength(roomId) > 0;

          // console.log(`   - 允许WebSocket播放: ${shouldAllowWebSocketPlay}`);

          // 新增：详细的判断说明
          if (!shouldAllowWebSocketPlay) {
            if (!isCurrentRoom) {
              console.log(
                `   - 跳过原因: 不是当前房间 (currentRoomId: ${this.currentRoomId})`
              );
            } else if (isPlaying) {
              console.log(`   - 跳过原因: 当前已在播放，新音频已进入队列等待`);
            }
          }

          if (shouldAllowWebSocketPlay) {
            // 若堆积过大，进行窗口化跳跃，避免长时间滞后
            try {
              const cfg = (getConfig && getConfig()) || ({} as any);
              const BACKLOG_THRESHOLD = Number(
                (state as any).backlogThreshold ??
                  cfg?.Audio?.BacklogThreshold ??
                  10
              );
              const WINDOW_MS = Number(
                (state as any).windowMs ?? cfg?.Audio?.WindowMs ?? 10_000
              );
              const WINDOW_TAIL = Number(
                (state as any).windowTail ?? cfg?.Audio?.WindowTail ?? 8
              );
              const qlen = this.getQueueLength(roomId);
              const backlog = qlen - (state.currentIndex ?? 0);
              const isActivelyPlaying = !!(
                (state as any).howlerPlayer &&
                (state as any).howlerPlayer.getStatus().isPlaying
              );
              if (
                backlog > BACKLOG_THRESHOLD &&
                qlen > 0 &&
                !isActivelyPlaying
              ) {
                const nowTs = Date.now();
                // 计算基于时间窗口的起点
                let idxByTime = qlen - 1;
                for (let i = 0; i < qlen; i++) {
                  const it: any = state.queue[i];
                  const age = nowTs - (it?.createTime || nowTs);
                  if (age <= WINDOW_MS) {
                    idxByTime = i;
                    break;
                  }
                }
                const idxByTail = Math.max(0, qlen - WINDOW_TAIL);
                let newStart = Math.min(idxByTime, idxByTail);
                // 确保不跨过任何紧急项：若区间内存在紧急项，则回退到最早的紧急项位置
                for (let i = state.currentIndex; i <= newStart; i++) {
                  if ((state.queue[i] as any)?.voiceNo === -1) {
                    newStart = i;
                    break;
                  }
                }

                if (newStart > state.currentIndex) {
                  // 注释窗口化跳跃日志
                  // console.warn(`⏩ 窗口化跳跃: ${state.currentIndex} -> ${newStart} (backlog=${backlog}, qlen=${qlen})`);
                  state.currentIndex = newStart;
                }
              }
            } catch {}
            // 触发前前向跳过：保证索引落在“可播项”
            try {
              // 🍎 后台或正在播放时，不在此处推进 currentIndex，避免打断当前句
              const isActivelyPlaying2 = !!(
                (state as any).howlerPlayer &&
                (state as any).howlerPlayer.getStatus().isPlaying
              );
              if (isActivelyPlaying2) {
                // 保留现有索引，由各播放器自然推进
              } else {
                const PROTECT_MS = Number(
                  (state as any).switchProtectMs ?? 5000
                );
                const inProtect =
                  !!state.lastProductSwitchTime &&
                  Date.now() - state.lastProductSwitchTime <= PROTECT_MS;
                while (state.currentIndex < this.getQueueLength(roomId)) {
                  const item = state.queue[state.currentIndex] as any;
                  const currentPid =
                    state.switchingProductId || state.selectedProductId || null;
                  const sameProduct = !!(
                    item?.productId &&
                    currentPid &&
                    item.productId === currentPid
                  );
                  const verMismatchRaw =
                    item?.version !== (state.switchVersion ?? 0);
                  // 仅当“不是当前产品”时，才因为版本不匹配而跳过；同一产品放行旧版本，保证连续
                  const verMismatch = verMismatchRaw && !sameProduct;
                  const wrongProduct =
                    state.switchingProductId &&
                    item?.productId &&
                    item.productId !== state.switchingProductId;
                  // 仅当消息无 productId 时，才用时间阈值在保护期内判“过旧”
                  const tooOldInProtect =
                    inProtect &&
                    !item?.productId &&
                    item?.createTime &&
                    item.createTime < state.lastProductSwitchTime!;
                  const noUrl = !item?.audioUrl;
                  if (verMismatch || wrongProduct || tooOldInProtect || noUrl) {
                    // 注释频繁的跳过项日志
                    // console.warn(
                    //   `⏭️ 跳过不可播项 idx=${state.currentIndex} verMis=${verMismatch} (raw=${verMismatchRaw}) wrongProduct=${!!wrongProduct} oldInProtect=${!!tooOldInProtect} noUrl=${!!noUrl} itemPid=${item?.productId || 'NA'} curPid=${currentPid || 'NA'} itemVer=${item?.version ?? 'NA'} curVer=${state.switchVersion ?? 0}`
                    // );
                    state.currentIndex++;
                    continue;
                  }
                  break;
                }
              }
            } catch {}
            // 清除playNext防抖，确保新音频能立即播放
            const playKey = `${roomId}_play_next`;
            if (state.lastLogTime && state.lastLogTime[playKey]) {
              delete state.lastLogTime[playKey];
            }
            // 注释频繁的播放触发日志
            // console.log(`🎵 WebSocket触发播放: 房间${roomId}, 队列长度${this.getQueueLength(roomId)}`);
            // 再次防御性校准
            if (state.currentIndex >= this.getQueueLength(roomId)) {
              state.currentIndex = Math.max(0, this.getQueueLength(roomId) - 1);
            }
            this.playNext(roomId);
          }
        } else if (data.waitingQueue) {
          const waitingQueue = JSON.parse(data.waitingQueue);
          if (!waitingQueue.length) {
            state.insertSegmentList = state.insertSegmentList.map(item => ({
              ...item,
              isInsert: true
            }));
            return;
          }
          state.insertSegmentList = state.insertSegmentList.map(item => {
            if (!waitingQueue.find(queueItem => queueItem.id === item.id)) {
              return {
                ...item,
                isInsert: true
              };
            }
            return item;
          });
        } else if (data.type === "ping") {
          ws.send(
            JSON.stringify({
              type: "pong",
              timestamp: Date.now(),
              roomId: params.roomId
            })
          );
        } else if (data.type === "error") {
          // 🔧 新增：关闭当前 WebSocket 并重新连接（带重试次数限制）
          console.error(`❌ 收到服务器错误消息: ${data.message}`);

          // 初始化错误重连计数器（独立于普通重连计数器）
          if (!state.audioQueueWsErrorReconnectCount) {
            state.audioQueueWsErrorReconnectCount = 0;
          }

          // 检查是否超过最大重试次数
          const MAX_ERROR_RECONNECT_ATTEMPTS = 5; // 最多重试5次

          if (
            state.audioQueueWsErrorReconnectCount >=
            MAX_ERROR_RECONNECT_ATTEMPTS
          ) {
            console.error(
              `❌ 错误重连次数已达上限(${MAX_ERROR_RECONNECT_ATTEMPTS}次)`
            );
            // message.error(
            //   data.message + `，${transformI18n($t("public.reconnectFailed"))}`,
            //   false
            // );

            // 清理 WebSocket 连接
            if (ws && ws.readyState === WebSocket.OPEN) {
              try {
                ws.close(1000, "Max error reconnect attempts reached");
              } catch (closeError) {
                console.error("关闭 WebSocket 时出错:", closeError);
              }
            }
            if ((state as any).audioQueueWsNoDataTimer) {
              clearTimeout((state as any).audioQueueWsNoDataTimer);
              (state as any).audioQueueWsNoDataTimer = null;
            }
            state.audioQueueWs = null;
            return;
          }

          // 增加重连计数
          state.audioQueueWsErrorReconnectCount++;

          // message.error(
          //   `${data.message}，${transformI18n($t("public.reconnectIng"))}(${state.audioQueueWsErrorReconnectCount}/${MAX_ERROR_RECONNECT_ATTEMPTS})`,
          //   false
          // );

          // 清理当前 WebSocket 连接
          if (ws && ws.readyState === WebSocket.OPEN) {
            try {
              ws.close(1000, "Server error, reconnecting");
            } catch (closeError) {
              console.error("关闭 WebSocket 时出错:", closeError);
            }
          }

          // 清除状态
          state.audioQueueWs = null;

          // 指数退避：延迟时间随重试次数增加
          const baseDelay = 2000; // 基础延迟2秒
          const reconnectDelay = Math.min(
            baseDelay * Math.pow(2, state.audioQueueWsErrorReconnectCount - 1),
            30000 // 最大延迟30秒
          );

          console.warn(
            `⏱️ ${reconnectDelay / 1000}秒后进行第${state.audioQueueWsErrorReconnectCount}次错误重连...`
          );

          setTimeout(() => {
            // 确保直播还在进行中才重连
            if (state.productList?.status === "live") {
              console.log(
                `🔄 开始第${state.audioQueueWsErrorReconnectCount}次错误重连...`
              );
              this.initAudioQueueWebSocket(roomId, sessionId, isExperience)
                .then(() => {
                  console.log(`✅ WebSocket 错误重连成功`);
                  // 重连成功后重置错误重连计数器
                  state.audioQueueWsErrorReconnectCount = 0;
                  // message.success($t("public.reconnectSuccess"), true);
                })
                .catch(error => {
                  console.error(
                    `❌ 第${state.audioQueueWsErrorReconnectCount}次错误重连失败:`,
                    error
                  );
                  // 如果还没到达重试上限，不显示错误提示（会自动重试）
                  if (
                    state.audioQueueWsErrorReconnectCount >=
                    MAX_ERROR_RECONNECT_ATTEMPTS
                  ) {
                    // message.error(
                    //   `${transformI18n($t("public.reconnectFailed"))}(${state.audioQueueWsErrorReconnectCount}/${MAX_ERROR_RECONNECT_ATTEMPTS})`,
                    //   false
                    // );
                  }
                });
            } else {
              console.warn(`⚠️ 直播已结束，取消错误重连`);
              state.audioQueueWsErrorReconnectCount = 0;
            }
          }, reconnectDelay);
        } else {
          console.warn(`⚠️ WebSocket消息数据不完整:`, data);
        }
      } catch (error) {
        console.error(`❌ WebSocket消息解析失败:`, error, event.data);
      }
    };

    ws.onerror = error => {
      console.warn(
        `Audio Queue WebSocket for room ${roomId} connection error:`,
        error
      );
      if ((state as any).audioQueueWsNoDataTimer) {
        clearTimeout((state as any).audioQueueWsNoDataTimer);
        (state as any).audioQueueWsNoDataTimer = null;
      }
      state.audioQueueWs = null;

      // 如果是首次连接失败，则reject
      if (!state.audioQueueWsReconnectCount) {
        // message.error("AI口播连接出错");
        reject(error);
      }
    };

    ws.onclose = event => {
      console.warn(
        `Audio Queue WebSocket for room ${roomId} Closed.`,
        event.code,
        event.reason || "No reason provided"
      );
      if (event.code === 1006) {
        try {
          const queueLen =
            (typeof this.getQueueLength === "function"
              ? this.getQueueLength(roomId)
              : state.queue?.length) ?? 0;
          const currentIdx = state.currentIndex ?? 0;
          const isPlaying = !!state.isPlaying;
          const pendingSwitch = !!state.pendingProductSwitch;
          let lockPid: string | null = null;
          try {
            const waState: any = (state as any).__appleWA__;
            lockPid =
              (waState &&
                (waState.justSwitchPid || waState.preparingTargetPid)) ||
              null;
          } catch {}
          console.warn(
            `[ws-1006] room=${roomId} queueLen=${queueLen} currentIdx=${currentIdx} isPlaying=${isPlaying} pendingSwitch=${pendingSwitch} lockPid=${lockPid}`
          );
        } catch (logError) {
          console.warn(`[ws-1006] logging failed for room ${roomId}`, logError);
        }
      }
      state.audioQueueWs = null;

      // 初始化重连计数器
      if (!state.audioQueueWsReconnectCount) {
        state.audioQueueWsReconnectCount = 0;
      }

      // 特殊处理：如果是正常关闭或认证失败，不要重连
      if (event.code === 1000 || event.code === 1008) {
        return;
      }

      if (state.productList?.status === "live") {
        // 开播时，音频队列socket断开就触发播放历史语音，避免出现播放中断情况
        emitter.emit("startPlayHistory");
      }

      // 如果重连次数过多，停止重连
      if (state.audioQueueWsReconnectCount >= 10) {
        // message.error($t("public.reconnectCountFailed"), true);
        return;
      }

      // 直播期间持续重连，直到直播结束
      if (state.productList?.status === "live") {
        ws.onmessage = null;
        state.audioQueueWsReconnectCount++;
        const retryDelay = Math.min(
          2000 * Math.pow(2, Math.min(state.audioQueueWsReconnectCount - 1, 4)), // 减少指数增长速度
          20000 // 增加最大延迟到20秒
        );

        console.warn(
          `🔄 AI口播连接断开 (错误代码: ${event.code})，${retryDelay / 1000}秒后进行第${state.audioQueueWsReconnectCount}次重连...`
        );

        setTimeout(() => {
          if (state.productList?.status === "live" && !state.audioQueueWs) {
            console.warn(
              `🔄 开始第${state.audioQueueWsReconnectCount}次重连AI口播WebSocket...`
            );
            this.initAudioQueueWebSocket(roomId, sessionId, isExperience).catch(
              error => {
                console.warn(
                  `第${state.audioQueueWsReconnectCount}次重连失败:`,
                  error
                );
              }
            );
          }
        }, retryDelay);
      } else {
      }
    };
  });
}

// 🔧 公屏WebSocket心跳机制
export function startCommentWsHeartbeat(
  this: RootState & { [key: string]: any }
) {
  if (!this.currentRoomState) return;
  const state = this.currentRoomState;

  // 清除之前的心跳定时器
  this.stopCommentWsHeartbeat();

  // 启动心跳定时器，每30秒发送一次心跳
  state.commentWsHeartbeatTimer = setInterval(() => {
    if (state.commentWs && state.commentWs.readyState === WebSocket.OPEN) {
      try {
        const pingMessage = {
          type: "ping",
          timestamp: Date.now()
        };
        state.commentWs.send(JSON.stringify(pingMessage));
        console.log("📡 发送公屏WebSocket心跳 (ping)");
      } catch (error) {
        console.error("❌ 发送公屏WebSocket心跳失败:", error);
        // 心跳发送失败，可能连接已断开，尝试重连
        this.bindCommentWebSocket().catch(() => {
          console.error("❌ 心跳失败后重连WebSocket失败");
        });
      }
    }
  }, 30000); // 30秒间隔

  console.log("💓 启动公屏WebSocket心跳机制");
}

export function stopCommentWsHeartbeat(
  this: RootState & { [key: string]: any }
) {
  if (!this.currentRoomState) return;
  const state = this.currentRoomState;

  if (state.commentWsHeartbeatTimer) {
    clearInterval(state.commentWsHeartbeatTimer);
    state.commentWsHeartbeatTimer = null;
    console.log("💔 停止公屏WebSocket心跳机制");
  }
}
